import { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { useHistory } from 'react-router-dom';

import { ROUTE_REGISTER } from '../../config/routes';
import Loading from '../Loading';
import useGoLockerDropOffStatus from './useGoLockerDropOffStatus';
import {
  G5_TITLES,
  PENDING_SYNC_COPY,
  PENDING_TIMELINE,
  STATUS_LOAD_ERROR_COPY,
  getDroppedOffState,
  getDroppedOffSubline,
  getDroppedOffTimeline,
  getPendingSubline,
  getRewardAmountLabel,
  isRewardAdded,
  isShipmentDelivered,
} from '../../utils/goLocker';
import TcIcon from '../../images/tc-balance-figma.svg';
import CheckFilledIcon from '../../images/icon-check-filled-green.svg';
import ClockDarkIcon from '../../images/icon-clock-dark.svg';
import ClockWhiteIcon from '../../images/icon-clock-white.svg';
import DashedCircleIcon from '../../images/icon-dashed-circle.svg';
import QrCodeWhiteIcon from '../../images/icon-qrcode-white.svg';
import CloseIcon from '../../images/icon-x-dark.svg';

import styles from './GoLockerDropOffStatus.module.scss';

/**
 * TrashieCash pill (Figma "TC Balance Pill" 6512:11393): the TC mark and, when the
 * amount is known, "+$2.00". Dimmed while the reward is pending (G6 12245:3495).
 */
const RewardPill = ({ amountLabel, dimmed }) => (
  <span className={`${styles.tcPill}${dimmed ? ` ${styles.tcPillDimmed}` : ''}`}>
    <img src={TcIcon} alt="Trashie Cash" width={20} height={20} />
    {amountLabel && <span className={styles.tcAmount}>{amountLabel}</span>}
  </span>
);

const PendingChip = () => <span className={styles.pendingChip}>Pending</span>;

const TimelineRow = ({ icon, title, body, muted }) => (
  <li className={`${styles.timelineRow}${muted ? ` ${styles.timelineRowMuted}` : ''}`}>
    <img src={icon} alt="" width={24} height={24} className={styles.timelineIcon} />
    <div className={styles.timelineText}>
      <span className={styles.timelineTitle}>{title}</span>
      <span className={styles.timelineBody}>{body}</span>
    </div>
  </li>
);

/**
 * GoLockerDropOffStatus — G6 "Waiting on the locker" (12245:3489) until GoLocker
 * reports the drop-off, then G5 in one of three frames following the bag's progress:
 *   Dropped off (12245:3411) → Picked up (12349:12473) → Delivered! (12349:12098)
 * (`dropOff.pickedUpAt`, then `deliveredAt` / shipmentStatus DELIVERED).
 * The TrashieCash chip follows `reward` in every G5 state: the drop-off (RECEIVED)
 * releases it, so it normally reads "+amount added to your balance" from Dropped
 * off on; a still-PENDING reward shows the coin and "Pending". G6 is always Pending.
 *
 * Two ways to feed it:
 *   - LIVE: pass only `code` (one bag) or `batchId`. The screen fetches and polls the
 *     bag's status itself (useGoLockerDropOffStatus), so it moves from state to state
 *     while open. This is what openGoLockerDropOffStatus uses.
 *   - STATIC: pass `dropOff` (null for G6) plus reward / deliveredAt / shipmentStatus /
 *     bagCount — a snapshot, no requests.
 * Plus `code` (single bag, masked for G6) and onClose.
 */
export const GoLockerDropOffStatus = ({
  dropOff: dropOffProp,
  reward: rewardProp,
  deliveredAt: deliveredAtProp,
  shipmentStatus: shipmentStatusProp,
  bagCount: bagCountProp = 1,
  code,
  batchId,
  onClose,
}) => {
  const history = useHistory();
  const isLive = dropOffProp === undefined && Boolean(code || batchId);
  const { status: live, failed, retry } = useGoLockerDropOffStatus({
    code, batchId, enabled: isLive,
  });

  // Live mode with no answer yet never falls through to G6: the only way in is View
  // Tracking on a dropped-off label, so "Waiting on the locker" from a network blip would
  // tell the customer their drop-off never registered. It shows the loader while the
  // first answer is pending, and a Retry once fetches keep failing. Both keep a close
  // button — on phones the dialog is full screen, with no backdrop or Esc to leave by.
  if (isLive && !live) {
    return (
      <div className={styles.screen}>
        <header className={styles.header}>
          <div className={styles.actions}>
            <span className={styles.actionsSpacer} />
            <span className={styles.actionsSpacer}>
              <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
                <img src={CloseIcon} alt="" width={18} height={18} />
              </button>
            </span>
          </div>
        </header>
        {failed ? (
          <>
            <div className={`${styles.content} ${styles.screenLoading}`}>
              <p className={styles.sectionTitle}>{STATUS_LOAD_ERROR_COPY.title}</p>
              <p className={styles.timelineBody}>{STATUS_LOAD_ERROR_COPY.body}</p>
            </div>
            <footer className={styles.actionBar}>
              <button type="button" className={styles.primary} onClick={retry}>Retry</button>
              <button type="button" className={styles.secondary} onClick={onClose}>Close</button>
            </footer>
          </>
        ) : (
          <div className={`${styles.content} ${styles.screenLoading}`}>
            <Loading size={40} />
          </div>
        )}
      </div>
    );
  }

  const dropOff = isLive ? (live?.dropOff ?? null) : dropOffProp;
  const reward = isLive ? (live?.reward ?? null) : rewardProp;
  const deliveredAt = isLive ? live?.deliveredAt : deliveredAtProp;
  const shipmentStatus = isLive ? live?.shipmentStatus : shipmentStatusProp;
  const bagCount = isLive ? (live?.bagCount ?? 1) : bagCountProp;

  // Past G6 once the drop-off is recorded — or the bag is delivered without one.
  const isDroppedOff = Boolean(dropOff) || isShipmentDelivered({ deliveredAt, shipmentStatus });
  const state = getDroppedOffState({ dropOff, deliveredAt, shipmentStatus });
  const rewardAdded = isRewardAdded(reward);

  const header = isDroppedOff ? (
    <>
      <img src={CheckFilledIcon} alt="" width={48} height={48} className={styles.heroIcon} />
      <h2 className={styles.title}>{G5_TITLES[state]}</h2>
      <p className={styles.subline}>{getDroppedOffSubline({ bagCount, dropOff })}</p>
      <div className={styles.rewardRow}>
        {rewardAdded ? (
          <>
            <RewardPill amountLabel={getRewardAmountLabel(reward)} />
            <span className={styles.rewardText}>added to your balance</span>
          </>
        ) : (
          <>
            <RewardPill dimmed />
            <PendingChip />
          </>
        )}
      </div>
    </>
  ) : (
    <>
      <span className={styles.heroClock}>
        <img src={ClockWhiteIcon} alt="" width={24} height={24} />
      </span>
      <h2 className={styles.title}>Waiting on the locker</h2>
      <p className={styles.subline}>
        {getPendingSubline({ bagCount, code })}
      </p>
      <div className={styles.rewardRow}>
        <RewardPill dimmed />
        <PendingChip />
      </div>
    </>
  );

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div className={styles.actions}>
          <span className={styles.actionsSpacer} />
          <span className={styles.pageTitle}>
            <img src={QrCodeWhiteIcon} alt="" width={24} height={24} />
            {isDroppedOff ? 'Drop-off Complete' : 'Drop-off Pending'}
          </span>
          <span className={styles.actionsSpacer}>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <img src={CloseIcon} alt="" width={18} height={18} />
            </button>
          </span>
        </div>
        <div className={styles.hero}>{header}</div>
      </header>

      <div className={styles.content}>
        {isDroppedOff ? (
          <>
            <p className={styles.sectionTitle}>What happens next</p>
            <ol className={styles.timeline}>
              {getDroppedOffTimeline({ dropOff, deliveredAt, shipmentStatus }).map((step) => (
                <TimelineRow
                  key={step.key}
                  icon={step.done ? CheckFilledIcon : DashedCircleIcon}
                  title={step.title}
                  body={step.body}
                  muted={!step.done}
                />
              ))}
            </ol>
          </>
        ) : (
          <>
            <div className={styles.syncBanner}>
              <p className={styles.syncTitle}>{PENDING_SYNC_COPY.title}</p>
              <p className={styles.syncBody}>{PENDING_SYNC_COPY.body}</p>
            </div>
            <ol className={styles.timeline}>
              {PENDING_TIMELINE.map((step) => (
                <TimelineRow
                  key={step.key}
                  icon={step.icon === 'clock' ? ClockDarkIcon : DashedCircleIcon}
                  title={step.title}
                  body={step.body}
                  muted={step.icon !== 'clock'}
                />
              ))}
            </ol>
          </>
        )}
      </div>

      <footer className={styles.actionBar}>
        <button type="button" className={styles.primary} onClick={onClose}>Done</button>
        {isDroppedOff && (
          <button
            type="button"
            className={styles.secondary}
            onClick={() => {
              onClose?.();
              history.push(ROUTE_REGISTER);
            }}
          >
            Register more bags
          </button>
        )}
      </footer>
    </div>
  );
};

/**
 * The status screen as a dialog: full screen on phones (the frames are full-screen
 * sheets), a 393px card on wider viewports.
 *
 * The last props seen while open stay rendered until the exit transition finishes:
 * the host clears its target (so `code` goes undefined) the moment it closes, and
 * unmounting the screen then would run MUI's fade-out on an empty paper.
 */
const GoLockerDropOffDialog = ({ open, onClose, ...statusProps }) => {
  const lastPropsRef = useRef(statusProps);
  if (open) lastPropsRef.current = statusProps;
  const [exited, setExited] = useState(!open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: styles.paper }}
      TransitionProps={{ onEnter: () => setExited(false), onExited: () => setExited(true) }}
      aria-label="Drop-off status"
    >
      {(open || !exited) && <GoLockerDropOffStatus {...lastPropsRef.current} onClose={onClose} />}
    </Dialog>
  );
};

export default GoLockerDropOffDialog;
